import React from 'react';
import { useIntl, defineMessages } from 'react-intl-next';
import { styled } from '@compiled/react';

import AkButton from '@atlaskit/button/loading-button';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { TooltipWithShortcut } from '@confluence/tooltip-shortcut';
import { EDITOR2_SHORTCUTS, getShortcutString } from '@confluence/shortcuts';
import type { PublishProps } from '@confluence/editor-features/entry-points/types';

const PUBLISH_HOTKEY_DESCRIPTION_ID = 'publish-hotkey-description';

type EmbeddedPublishButtonProps = {
	onClickPublish: (arg: PublishProps) => () => void;
	publishButtonDisabled?: boolean;
	savingInProgress?: boolean;
	isUnpublishedDraft: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonWrapper = styled.span({
	display: 'inline-block',
});

const i18n = defineMessages({
	publishButtonText: {
		id: 'embedded-publish-button.publish-button-text',
		defaultMessage: 'Publish',
		description: 'Text for publish button',
	},
	updateButtonText: {
		id: 'embedded-publish-button.update-button-text',
		defaultMessage: 'Update',
		description: 'Text for update button',
	},
	publishHotkeyDescription: {
		id: 'embedded-publish-button.publish-hotkey-description',
		defaultMessage: 'To publish this content, select {keyCombination} on your keyboard. ',
		description: "Text used by screen reader to announce publish button's hotkey",
	},
});

export const EmbeddedPublishButton = ({
	onClickPublish,
	publishButtonDisabled,
	savingInProgress,
	isUnpublishedDraft,
}: EmbeddedPublishButtonProps) => {
	const intl = useIntl();
	const publishButtonText = isUnpublishedDraft
		? intl.formatMessage(i18n.publishButtonText)
		: intl.formatMessage(i18n.updateButtonText);
	return (
		<TooltipWithShortcut
			shortcutAccelerator={EDITOR2_SHORTCUTS.PUBLISH}
			shortcutListener={onClickPublish({ suppressNotifyWatchers: false })}
			position="bottom"
			isDisabled={publishButtonDisabled}
		>
			<VisuallyHidden id={PUBLISH_HOTKEY_DESCRIPTION_ID} aria-hidden="true">
				<span data-test-id="publish-screen-reader-hotkey-description">
					{intl.formatMessage(i18n.publishHotkeyDescription, {
						keyCombination: getShortcutString(EDITOR2_SHORTCUTS.PUBLISH, intl.formatMessage),
					})}
				</span>
			</VisuallyHidden>
			<ButtonWrapper data-test-id="publish-button">
				<AkButton
					id="publish-button"
					data-test-id="publish-button-ui"
					appearance="primary"
					onClick={onClickPublish({ suppressNotifyWatchers: false })}
					isDisabled={!savingInProgress && publishButtonDisabled}
					aria-describedby={PUBLISH_HOTKEY_DESCRIPTION_ID}
					isLoading={savingInProgress}
				>
					{publishButtonText}
				</AkButton>
			</ButtonWrapper>
		</TooltipWithShortcut>
	);
};
